import { Box, Typography, useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import HoverItemDetails from '@/features/VOD/components/itemDetails/HoverItemDetails'
import { ProgressBar } from '@/features'
import { VodItemLabel } from '@/features/VOD/components/VodItemLabel'
import { useVODStore } from '@/shared/store'
import placeholderPoster from '@/shared/assets/images/placeholderPoster.webp'
import { vodType } from '@/shared/constants'
import { VodRowItemTVProps } from '@/appTV/VOD/types/VodItemTVType'
import { useVodItemViewLogic } from '@/features/VOD/hooks/useVodItemViewLogic'
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation'
import {
    selectIsVodDetailsPageRendered,
    selectSetIsLinkedFromHome,
} from '@/shared/store/storeSelectors/vodSelectors'
import { isSmartTV } from '@/shared/lib'

const VodRowItemTV = ({
    vodItemHeight,
    item,
    handleGoToCategoryButtonClick,
    scrollPosition,
    hideCaptions,
    hideProgress,
    rowHeightPercentage,
    cardRadiusDp,
    isHomeContentItem,
    onFocusProp,
    onReady,
    shouldMemorizeLastFocusedItem = true,
    index,
}: VodRowItemTVProps) => {
    const {
        watchingProgress,
        itemAspectRatio,
        handleMouseEnter,
        handleMouseLeave,
        handleItemClick,
        details,
        imageUrl,
        title,
        label,
    } = useVodItemViewLogic(item, handleGoToCategoryButtonClick)

    const theme = useTheme()
    const setIsLinkedFromHome = useVODStore(selectSetIsLinkedFromHome)
    const isVodDetailsPageRendered = useVODStore(selectIsVodDetailsPageRendered)
    const handleClick = (focusKey: string) => {
        handleItemClick(focusKey, shouldMemorizeLastFocusedItem)
        isHomeContentItem && setIsLinkedFromHome(true)
    }

    const focusKey = item.title + index

    const { ref, focused } = useFocusable({
        focusKey,
        onEnterPress: () => {
            handleClick(focusKey)
        },
        onFocus: onFocusProp,
    })

    useEffect(() => {
        if (onReady && !isVodDetailsPageRendered) onReady()
    }, [onReady, focused])

    const isVod = item.assetType === vodType
    const displayProgressBar = !hideProgress && isVod
    const itemHeight = rowHeightPercentage
        ? vodItemHeight * (rowHeightPercentage / 100)
        : vodItemHeight

    const itemBoxStyle = {
        marginRight: '40px',
        scrollSnapAlign: 'end',
        position: 'relative',
        '&:hover': {
            cursor: 'pointer',
        },
        border: focused && isSmartTV() ? '5px solid red' : '',
    }

    const titleStyle = {
        textAlign: 'center',
        typography: 'label2',
        marginTop: '31px',
        color: 'white',
    }
    const vodContentStyle = {
        height: `${itemHeight}px`,
        width: `${
            itemAspectRatio ? itemHeight * itemAspectRatio : itemHeight
        }px`,
        borderRadius: cardRadiusDp
            ? `${cardRadiusDp}px`
            : theme.shape.borderRadius,
        overflow: 'hidden',
    }
    const progressBarWrapper = {
        position: 'absolute',
        bottom: '20px',
        left: '15%',
        width: '70%',
    }
    const labelStyle = {
        position: 'absolute',
        top: '-20px',
        right: '0px',
    }
    const imageStyle = {
        position: 'relative',
        height: '100%',
        width: '100%',
    }
    const hoverImageStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        transition: 'opacity 0.3s ease-in-out, backdropFilter 0.4s ease-in-out',
        opacity: 0,
        '&:hover': {
            backdropFilter: 'blur(10px)',
            opacity: 1,
        },
        overflow: 'hidden',
    }

    return (
        <>
            <Box
                ref={ref}
                sx={itemBoxStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleClick(focusKey)}>
                <Box sx={vodContentStyle}>
                    <Box position="relative" sx={imageStyle}>
                        <Box sx={hoverImageStyle}>
                            {details && <HoverItemDetails details={details} />}
                        </Box>
                        <LazyLoadImage
                            alt={item.title || 'movie poster'}
                            src={imageUrl}
                            height={itemHeight}
                            width={
                                itemAspectRatio
                                    ? itemHeight * itemAspectRatio
                                    : itemHeight
                            }
                            style={{
                                objectFit: 'contain',
                            }}
                            scrollPosition={scrollPosition}
                            placeholderSrc={placeholderPoster}
                            onError={(event) => {
                                const target = event.target as HTMLImageElement
                                target.onerror = null
                                target.src = placeholderPoster
                            }}
                        />
                        {displayProgressBar && (
                            <Box sx={progressBarWrapper}>
                                <ProgressBar
                                    value={watchingProgress}
                                    min={1}
                                    max={100}
                                    width={100}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
                {!hideCaptions && (
                    <Typography sx={titleStyle}>{title}</Typography>
                )}
            </Box>

            {label && (
                <Box sx={labelStyle}>
                    <VodItemLabel label={label} />
                </Box>
            )}
        </>
    )
}

export default VodRowItemTV
